import {
  edkt,
  checkConsentStatus,
  waitForConsent,
  matchedAudienceStore,
} from './edgekit';
import { logAnalytics, logDwellAnalytics } from './analytics';
import { registerBidResponseEventCallback } from './analytics/bid-event-stream';
import { pageDwellListener } from './analytics/dwell';
import {
  debug,
  logError,
  generateLoadId,
  getPagesAndAudiences,
  hasCmpBeenShownAndConsentRejected,
  makeServerSideMatchedAudiences,
} from './utils';
import { SequenceAnalytics } from './analytics/sequences';
import { pageViewAnalytics } from './analytics/pageviews';

declare const VERSION: string;
declare const EDKT_VERSION: string;

const MIQ_VENDOR_ID = 101;
const vendorIds = [MIQ_VENDOR_ID];
const edktInitializor = (window as any).edktInitializor || {};
const apiKey = edktInitializor.apiKey || '';
const accountId = edktInitializor.accountId || '';
const publisherId = edktInitializor.publisherId || '';

const CLIENTS_TO_OMIT_GDPR_CONSENT = ['pqIdXN', 'lKwi7g'];
const omitGdprConsent =
  CLIENTS_TO_OMIT_GDPR_CONSENT.includes(accountId) ||
  !!edktInitializor.disableGdpr;

const loadId = generateLoadId();
edktInitializor.loadId = loadId;

const run = async (): Promise<void> => {
  let hasConsent = false;

  edktInitializor.debug = {
    omitGdprConsent,
    hasConsent,
    VERSION,
    EDKT_VERSION,
  };

  if (window.location.href.includes('edktDebug=true')) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    (window as any).edkt = (window as any).edkt || edkt;
  }

  if (omitGdprConsent) {
    hasConsent = true;
  } else {
    let consentStatus;
    try {
      consentStatus = await checkConsentStatus(vendorIds);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      throw new Error(errorMessage);
    }
    if (!hasConsent && hasCmpBeenShownAndConsentRejected(consentStatus)) {
      await logAnalytics(
        consentStatus.hasConsent,
        edkt.getMatchedAudiences(),
        false
      );

      edkt.removeLocalData();
      pageViewAnalytics.removeLocalData();
      SequenceAnalytics.removeLocalData();

      debug('No consent granted', { ...edktInitializor.debug });

      return;
    }
  }

  hasConsent = hasConsent ? true : await waitForConsent(vendorIds);

  if (!hasConsent) {
    await logAnalytics(hasConsent, edkt.getMatchedAudiences(), false);

    edkt.removeLocalData();
    pageViewAnalytics.removeLocalData();
    SequenceAnalytics.removeLocalData();

    debug('Waited for consent, not granted', { ...edktInitializor.debug });
    return;
  }

  const { pagesResponse, audiencesResponse } = await getPagesAndAudiences(
    apiKey,
    accountId,
    publisherId,
    loadId
  );

  debug('pagesResponse: ', pagesResponse);
  debug('audiencesResponse: ', audiencesResponse);

  await edkt.run({
    pageFeatures: pagesResponse.data?.features,
    pageMetadata: pagesResponse.data?.metadata,
    audienceDefinitions: audiencesResponse.data || [],
    vendorIds,
    omitGdprConsent,
  });

  const serverMatchedAudiences = pagesResponse.data?.ma;
  if (
    Array.isArray(serverMatchedAudiences) &&
    serverMatchedAudiences.length > 0
  ) {
    const previouslyMatchedAudiences = edkt.getMatchedAudiences();
    const matchedAudiences = makeServerSideMatchedAudiences(
      serverMatchedAudiences,
      previouslyMatchedAudiences
    );
    matchedAudienceStore.setAudiences(matchedAudiences);
  }

  debug('matchedAudiences: ', edkt.getMatchedAudiences());

  await Promise.all([
    registerBidResponseEventCallback(apiKey, loadId),
    logAnalytics(
      hasConsent,
      edkt.getMatchedAudiences(),
      pagesResponse.data?.featuresAvailable,
      pagesResponse,
      audiencesResponse
    ),
  ]);

  pageDwellListener.listen((dwell: number) => {
    logDwellAnalytics(dwell);
  });
};

(async (): Promise<void> => {
  try {
    await run();
  } catch (err: any) {
    await logError(loadId, apiKey, err, VERSION);
  }
})();
